// Picker de fondo REDUCIDO del modo "Fotos de producto".
//
// El `FondoPicker` completo (biblioteca + guardados + subida + automático)
// es un sheet de 2.000 líneas pensado para escenas con modelo. Acá el modo
// ofrece solo dos caminos, así que van como dos cards inline:
//
//   • Fondo liso            → blanco (default): background_mode 'blanco' +
//                             background_id 5, el flujo clásico. Los otros
//                             colores (beige claro / rosa claro / negro)
//                             viajan como background_mode 'descripcion' con
//                             un prompt fijo (liso, sin texturas, luz de
//                             estudio) — no existen como filas en `fondos`.
//   • Describir escena      → background_mode 'descripcion' + background_text
//
// Hubo una tercera card "Copiar imagen de producto" deshabilitada con badge
// "Pronto" (ADR-2026-08-13-modo-fotos-producto la mostraba como señal de
// hacia dónde iba el modo); se quitó a pedido de Mati el 2026-08-30 — si la
// feature llega, la card vuelve acá.
//
// El tipo de selección es el `FondoSelection` compartido con el resto de V2
// (restringido a sus dos variantes soportadas), así el armado del payload en
// InterfazV2 es el MISMO helper que usan design-modelo y el catálogo.

import React, { useEffect, useRef } from 'react';
import { Check, Type as TypeIcon } from 'lucide-react';
import type { FondoSelection } from '../FondoPicker';
import { PromptRecientesButton } from './PromptRecientesButton';

// El catálogo de colores vive en `../coloresDeFondoLiso` desde que Modelo y Catálogo
// también lo usan (ADR-2026-09-15-el-fondo-liso-con-color-llega-a-modelo). Se
// re-exporta acá porque este módulo era su casa y varios lo importan de esta ruta.
import { COLORES_FONDO_LISO, type ColorFondoLiso } from '../coloresDeFondoLiso';
export { COLORES_FONDO_LISO, promptDeFondoLiso } from '../coloresDeFondoLiso';
export type { ColorFondoLiso } from '../coloresDeFondoLiso';


/** Subconjunto de `FondoSelection` que soporta este modo. El color del liso
 *  viaja DENTRO de la selección (como `temperatura`/`iluminacion` del blanco
 *  clásico) para que el restore de la config lo traiga solo. */
export type FondoProductoSelection =
  | { mode: 'blanco'; colorLiso?: ColorFondoLiso }
  | { mode: 'descripcion'; text: string };

/** Type guard para estrechar un `FondoSelection` genérico (el state de
 *  InterfazV2 es compartido con los otros modos) al subconjunto de acá. */
export const esFondoProducto = (
  sel: FondoSelection | null,
): sel is FondoProductoSelection =>
  sel?.mode === 'blanco' || sel?.mode === 'descripcion';

export interface FondoProductoPickerProps {
  selection: FondoProductoSelection;
  onChange: (sel: FondoProductoSelection) => void;
  /** Nombres de los productos elegidos — alimentan el placeholder del
   *  textarea con un ejemplo concreto ("Sobre una mesa de mármol…"). */
  productNames?: string[];
}

export const FondoProductoPicker: React.FC<FondoProductoPickerProps> = ({
  selection,
  onChange,
  productNames = [],
}) => {
  const esBlanco = selection.mode === 'blanco';
  const esDescripcion = selection.mode === 'descripcion';
  const texto = selection.mode === 'descripcion' ? selection.text : '';
  const colorActivo: ColorFondoLiso =
    selection.mode === 'blanco' ? (selection.colorLiso ?? 'blanco') : 'blanco';
  const colorActivoMeta =
    COLORES_FONDO_LISO.find((c) => c.id === colorActivo) ??
    COLORES_FONDO_LISO[0];

  // Auto-focus del textarea al elegir "Describir escena": la card y el campo
  // son un solo gesto, sin este foco había que tocar dos veces.
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const wasDescripcion = useRef(esDescripcion);
  useEffect(() => {
    if (esDescripcion && !wasDescripcion.current) textareaRef.current?.focus();
    wasDescripcion.current = esDescripcion;
  }, [esDescripcion]);

  const placeholder = productNames[0]
    ? `Ej: ${productNames[0]} sobre una mesa de mármol, con luz natural entrando de costado.`
    : 'Ej: sobre una mesa de mármol, con luz natural entrando de costado.';

  return (
    <div className="flex flex-col gap-2.5">
      <div className="grid grid-cols-2 gap-2.5">
        {/* Fondo liso — el mini-cuadrado muestra el color activo. Elegir la
            card conserva el color que ya estaba elegido. */}
        <button
          type="button"
          onClick={() => onChange({ mode: 'blanco', colorLiso: colorActivo })}
          aria-pressed={esBlanco}
          className={`relative h-24 rounded-2xl bg-white border-2 text-left transition-colors active:scale-[0.98] ${
            esBlanco
              ? 'border-brand-primary'
              : 'border-neutral-200 hover:border-[#1644EB]/40'
          }`}
        >
          <div className="h-full flex flex-col items-start justify-between p-3">
            <span
              className="inline-flex w-9 h-9 rounded-md border-2 border-neutral-300"
              style={{ backgroundColor: colorActivoMeta.hex }}
            />
            <span className="text-[14px] font-semibold text-brand-black/85 text-left leading-tight">
              Fondo liso
            </span>
          </div>
          {esBlanco && (
            <span className="absolute top-2 right-2 w-6 h-6 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </button>

        {/* Describir escena */}
        <button
          type="button"
          onClick={() => onChange({ mode: 'descripcion', text: texto })}
          aria-pressed={esDescripcion}
          className={`relative h-24 rounded-2xl bg-white border-2 text-left transition-colors active:scale-[0.98] ${
            esDescripcion
              ? 'border-brand-primary'
              : 'border-neutral-200 hover:border-[#1644EB]/40'
          }`}
        >
          <div className="h-full flex flex-col items-start justify-between p-3">
            <span className="w-9 h-9 rounded-full bg-[#1644EB]/10 inline-flex items-center justify-center">
              <TypeIcon className="w-4 h-4 text-brand-primary" />
            </span>
            <span className="text-[14px] font-semibold text-brand-black/85 text-left leading-tight">
              Describir escena
            </span>
          </div>
          {esDescripcion && (
            <span className="absolute top-2 right-2 w-6 h-6 rounded-full inline-flex items-center justify-center bg-brand-primary text-white shadow-md">
              <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </span>
          )}
        </button>

      </div>

      {/* Swatches de color — solo con "Fondo liso" activo. Todos lisos, sin
          texturas, con luz de estudio (el prompt fijo vive en el catálogo).
          Van CONTENIDOS en una card (mismo lenguaje que el textarea de la
          escena) y en una grilla de 4 columnas iguales: la distribución queda
          pareja tanto en el panel angosto de mobile como en la columna ancha
          de desktop, y el padding del contenedor le da aire al ring de
          selección (ring-offset) para que no roce los bordes. */}
      {esBlanco && (
        <div className="rounded-2xl bg-white border border-neutral-200 px-3 py-3 grid grid-cols-4">
          {COLORES_FONDO_LISO.map((c) => {
            const activo = colorActivo === c.id;
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => onChange({ mode: 'blanco', colorLiso: c.id })}
                aria-pressed={activo}
                className="group flex flex-col items-center gap-2 py-1.5"
              >
                <span
                  className={`w-10 h-10 rounded-full border transition-shadow ${
                    activo
                      ? 'border-transparent ring-2 ring-brand-primary ring-offset-2'
                      : 'border-neutral-300 group-hover:ring-2 group-hover:ring-brand-primary/30 group-hover:ring-offset-2'
                  }`}
                  style={{ backgroundColor: c.hex }}
                />
                <span
                  className={`text-[11.5px] leading-tight transition-colors ${
                    activo
                      ? 'font-semibold text-brand-black/85'
                      : 'font-medium text-[#3C3C43]/55'
                  }`}
                >
                  {c.label}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {/* Textarea de la escena — solo con "Describir escena" activa. */}
      {esDescripcion && (
        <div className="relative">
          <textarea
            ref={textareaRef}
            value={texto}
            onChange={(e) =>
              onChange({ mode: 'descripcion', text: e.target.value })
            }
            placeholder={placeholder}
            rows={3}
            className="w-full resize-none px-4 pt-3 pb-11 rounded-2xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
          />
          <PromptRecientesButton
            tipo="escena"
            onPick={(t) => onChange({ mode: 'descripcion', text: t })}
            titulo="Escenas usadas antes"
            className="absolute bottom-2.5 right-2.5"
          />
        </div>
      )}
    </div>
  );
};
